import React, { useCallback, useEffect, useRef, useState } from 'react';
import { PlusOutlined } from '@ant-design/icons';
import type { InputRef } from 'antd';
import { Flex, Input, Tag, theme, Tooltip } from 'antd';

const tagInputStyle: React.CSSProperties = {
  width: 64,
  height: 22,
  marginInlineEnd: 8,
  verticalAlign: 'top',
};

// Light color palette for tags
const tagColors = [
  '#e6f7ff', // light blue
  '#f6ffed', // light green
  '#fff7e6', // light orange
  '#fff1f0', // light red
  '#f9f0ff', // light purple
  '#fcffe6', // light yellow
  '#f0f5ff', // light indigo
  '#fff2e8', // light coral
  '#f6ffed', // light mint
  '#e6fffb', // light cyan
];

interface SearchTermTagFieldProps {
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  maxTags?: number;
}

export const SearchTermTagField: React.FC<SearchTermTagFieldProps> = ({
  value = '',
  onChange,
  placeholder = 'Add search terms...',
  disabled = false,
  maxTags = 10,
}) => {
  const { token } = theme.useToken();
  const [tags, setTags] = useState<string[]>([]);
  const [inputVisible, setInputVisible] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [editInputIndex, setEditInputIndex] = useState(-1);
  const [editInputValue, setEditInputValue] = useState('');
  const inputRef = useRef<InputRef>(null);
  const editInputRef = useRef<InputRef>(null);

  // Initialize tags from value prop
  useEffect(() => {
    if (value) {
      const tagArray = value
        .split(',')
        .map(tag => tag.trim())
        .filter(tag => tag.length > 0);
      setTags(tagArray);
    } else {
      setTags([]);
    }
  }, [value]);

  // Update parent form when tags change
  const updateParentForm = useCallback(
    (newTags: string[]) => {
      const tagString = newTags.join(', ');
      if (onChange && tagString !== value) {
        onChange(tagString);
      }
    },
    [onChange, value]
  );

  useEffect(() => {
    if (inputVisible) {
      inputRef.current?.focus();
    }
  }, [inputVisible]);

  useEffect(() => {
    editInputRef.current?.focus();
  }, [editInputValue]);

  const handleClose = (removedTag: string) => {
    const newTags = tags.filter(tag => tag !== removedTag);
    setTags(newTags);
    updateParentForm(newTags);
  };

  const showInput = () => {
    if (tags.length >= maxTags) return;
    setInputVisible(true);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const handleInputConfirm = () => {
    if (inputValue && !tags.includes(inputValue) && tags.length < maxTags) {
      const newTags = [...tags, inputValue];
      setTags(newTags);
      updateParentForm(newTags);
    }
    setInputVisible(false);
    setInputValue('');
  };

  const handleEditInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditInputValue(e.target.value);
  };

  const handleEditInputConfirm = () => {
    const newTags = [...tags];
    newTags[editInputIndex] = editInputValue;
    setTags(newTags);
    updateParentForm(newTags);
    setEditInputIndex(-1);
    setEditInputValue('');
  };

  const tagPlusStyle: React.CSSProperties = {
    height: 22,
    background: token.colorBgContainer,
    borderStyle: 'dashed',
  };

  const getTagColor = (index: number) => {
    return tagColors[index % tagColors.length];
  };

  return (
    <Flex gap="4px 0" wrap>
      {tags.map<React.ReactNode>((tag, index) => {
        if (editInputIndex === index) {
          return (
            <Input
              ref={editInputRef}
              key={tag}
              size="small"
              style={tagInputStyle}
              value={editInputValue}
              onChange={handleEditInputChange}
              onBlur={handleEditInputConfirm}
              onPressEnter={handleEditInputConfirm}
              disabled={disabled}
            />
          );
        }
        const isLongTag = tag.length > 20;
        const tagElem = (
          <Tag
            key={tag}
            closable={!disabled}
            style={{
              userSelect: 'none',
              backgroundColor: getTagColor(index),
              borderColor: getTagColor(index),
              color: '#000',
            }}
            onClose={() => handleClose(tag)}
          >
            <span
              onDoubleClick={e => {
                if (!disabled) {
                  setEditInputIndex(index);
                  setEditInputValue(tag);
                  e.preventDefault();
                }
              }}
            >
              {isLongTag ? `${tag.slice(0, 20)}...` : tag}
            </span>
          </Tag>
        );
        return isLongTag ? (
          <Tooltip title={tag} key={tag}>
            {tagElem}
          </Tooltip>
        ) : (
          tagElem
        );
      })}
      {inputVisible ? (
        <Input
          ref={inputRef}
          type="text"
          size="small"
          style={tagInputStyle}
          value={inputValue}
          onChange={handleInputChange}
          onBlur={handleInputConfirm}
          onPressEnter={handleInputConfirm}
          disabled={disabled}
        />
      ) : (
        tags.length < maxTags &&
        !disabled && (
          <Tag style={tagPlusStyle} icon={<PlusOutlined />} onClick={showInput}>
            {placeholder}
          </Tag>
        )
      )}
    </Flex>
  );
};
